// Relative imports (not `@/`) so bun can test this module.
import type { HexTileMap } from '../hexmap/model/types';
import type { RuleSet } from '../rules';
import {
  combatView,
  type EntityId,
  type PlayerId,
  type SimState,
  type TaskLine,
  type WorkerPlace,
  workBoard,
} from '../sim';
import { type WorkerIndex, workerIndex } from './activity';
import { healings } from './heals';
import { statusEnv } from './unitStatus';
import { buildRoster, type Roster } from './workerRoster';

interface Ctx {
  terrain: HexTileMap;
  rules: RuleSet;
}

/**
 * `compute` remembered for one tick, so the layers and panels that all want
 * it each tick (who fights, who heals, who works where) share one pass. Keyed
 * on the state object and its tick, like `viewerState`: the host's runner
 * changes one state in place, but only inside `tick()`, which advances
 * `state.tick`; a peer adopts a new object with every snapshot. `keys` are
 * the parts of the argument compared by identity (a `ctx` is often a fresh
 * object around the same terrain and rules).
 */
function perTick<A, T>(
  compute: (state: SimState, arg: A) => T,
  keys: (arg: A) => readonly unknown[]
): (state: SimState, arg: A) => T {
  let last: { state: SimState; tick: number; keys: readonly unknown[]; value: T } | null = null;
  return (state, arg) => {
    const k = keys(arg);
    if (
      last &&
      last.state === state &&
      last.tick === state.tick &&
      last.keys.length === k.length &&
      last.keys.every((v, i) => v === k[i])
    ) {
      return last.value;
    }
    const value = compute(state, arg);
    last = { state, tick: state.tick, keys: k, value };
    return value;
  };
}

const ctxKeys = (ctx: Ctx) => [ctx.terrain, ctx.rules];
const rulesKeys = (rules: RuleSet) => [rules];

const tickCombat = perTick(combatView, ctxKeys);

/** Sim `engagements` (attacker id → target id) for this tick. */
export function tickEngagements(state: SimState, ctx: Ctx): ReadonlyMap<EntityId, EntityId> {
  return tickCombat(state, ctx).engagements;
}

/** Sim `shelteredUnits` for this tick. */
export function tickSheltered(state: SimState, ctx: Ctx): ReadonlySet<EntityId> {
  return tickCombat(state, ctx).sheltered;
}

/** `healings` (healer id → patient id) for this tick. */
export const tickHealings: (state: SimState, rules: RuleSet) => ReadonlyMap<EntityId, EntityId> =
  perTick(healings, rulesKeys);

/** `workerIndex` for this tick, for `assignedWorkers`. */
export const tickWorkers: (state: SimState, rules: RuleSet) => WorkerIndex = perTick(
  workerIndex,
  rulesKeys
);

const tickBoard = perTick(
  (state: SimState, arg: { ctx: Ctx; cityId: EntityId }) => workBoard(state, arg.ctx, arg.cityId),
  (arg) => [arg.ctx.terrain, arg.ctx.rules, arg.cityId]
);

/**
 * The sim's `workBoard` (task lines and where each worker stands) for `cityId`
 * this tick: it builds a fresh automation cache, so the Workers tab reads it
 * through here rather than per render. One city is remembered at a time.
 */
export function tickWorkBoard(
  state: SimState,
  ctx: Ctx,
  cityId: EntityId
): { lines: TaskLine[]; places: WorkerPlace[]; away: WorkerPlace[] } {
  return tickBoard(state, { ctx, cityId });
}

/**
 * The Workers roster (`workerRoster.ts`) for `playerId` this tick, shared by
 * the roster panel and the idle button. Statuses name no player, so the
 * names aren't needed.
 */
export const tickRoster: (state: SimState, arg: Ctx & { playerId: PlayerId }) => Roster = perTick(
  (state, { terrain, rules, playerId }) => {
    const ctx = { terrain, rules };
    const env = statusEnv(state, ctx, playerId, (id) => id, tickHealings(state, rules));
    return buildRoster(state, ctx, playerId, env, tickWorkers(state, rules));
  },
  (arg) => [arg.terrain, arg.rules, arg.playerId]
);
